// Shared sidebar nav structure. Source of truth for the admin portal's
// menu organisation (Layout.tsx) and the read-only Permissions Matrix
// page that visualises the same data as a role-vs-route table.
//
// Keeping this in one place means a new admin page only needs to be
// added here once and both surfaces update together. Server-side
// `requireRole(...)` middleware remains authoritative for actual
// authorisation — these arrays are UX presentation, not security.

import {
  LayoutDashboard, ShoppingCart, Package, Tag, Megaphone,
  Users, UserCog, Bell, Settings, Star, Trophy, Gift,
  Truck, Radio, BarChart3, Flag, Map, Activity, PartyPopper, CalendarDays,
  Sparkles, LineChart, TrendingUp, Bot, MessageSquare, BookOpen,
  Globe, Share2, FileSearch, Ticket, Lock, Clock, Store, Building2, ClipboardList, Warehouse, Receipt,
  FileBarChart, Banknote, Trash2, Layers, Shield,
} from 'lucide-react'
import { ALL, OPS, OPS_ACCT, OPS_MKT, type AdminRole } from './admin-roles'

export type NavItem = {
  to: string
  icon: React.ComponentType<{ size?: number }>
  label: string
  badgeKey?: 'issues' | 'support'
  roles?: AdminRole[]            // undefined = visible to all roles
  superAdminOnly?: boolean       // legacy shorthand, equivalent to roles: ['super_admin']
}
export type NavSection = { id: string; label: string; items: NavItem[] }

export const SECTIONS: NavSection[] = [
  {
    id: 'insights',
    label: 'Insights',
    items: [
      { to: '/dashboard',        icon: LayoutDashboard, label: 'Dashboard',         roles: ALL },
      { to: '/sales',            icon: LineChart,       label: 'Sales',             roles: OPS_ACCT },
      { to: '/customer-trends',  icon: TrendingUp,      label: 'Customer Trends',   roles: ALL },
      { to: '/abandoned-carts',  icon: ShoppingCart,    label: 'Abandoned Carts',   roles: OPS_MKT },
      { to: '/performance',      icon: BarChart3,       label: 'Performance',       roles: OPS_ACCT },
      { to: '/games-analytics',  icon: Activity,        label: 'Games Analytics',   roles: OPS_MKT },
      { to: '/ai-logs',          icon: Bot,             label: 'DrinX AI',          superAdminOnly: true },
    ],
  },
  {
    id: 'commerce',
    label: 'Commerce',
    items: [
      { to: '/orders',         icon: ShoppingCart, label: 'Orders',           roles: OPS_ACCT },
      { to: '/products',       icon: Package,      label: 'Products',         roles: OPS },
      { to: '/categories',     icon: Tag,          label: 'Categories',       roles: OPS },
      { to: '/addons',         icon: Package,      label: 'Add-ons',          roles: OPS },
      { to: '/promotions',     icon: Megaphone,    label: 'Promotions',       roles: OPS_MKT },
      { to: '/recommendations',icon: Sparkles,     label: 'Recommendations',  roles: OPS_MKT },
      { to: '/discount-codes', icon: Tag,          label: 'Discount Codes',   roles: OPS_MKT },
      { to: '/bundles',        icon: Layers,       label: 'Bundles',          roles: OPS_MKT },
      { to: '/gift-cards',     icon: Gift,         label: 'Gift Cards',       roles: OPS_ACCT },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    items: [
      { to: '/customers', icon: Users,  label: 'Customers', roles: OPS_ACCT },
      { to: '/loyalty',   icon: Star,   label: 'Loyalty',   roles: OPS_ACCT },
      { to: '/referrals', icon: Share2, label: 'Referrals', roles: OPS_ACCT },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { to: '/dispatch', icon: Radio, label: 'Dispatch',       roles: OPS },
      { to: '/live-map', icon: Map,   label: 'Live Map',       roles: OPS },
      { to: '/drivers',  icon: Truck, label: 'Ambassadors',    roles: OPS },
      { to: '/stores',    icon: Store,        label: 'Stores',         roles: OPS },
      { to: '/suppliers',       icon: Building2,     label: 'Suppliers',       roles: OPS },
      { to: '/purchase-orders', icon: ClipboardList, label: 'Purchase Orders', roles: OPS },
      { to: '/stock-counts',    icon: Warehouse,     label: 'Stock Counts',    roles: OPS },
      { to: '/stock-transfers', icon: Truck,         label: 'Stock Transfers', roles: OPS },
      { to: '/cashiers',  icon: Lock,         label: 'Cashiers (POS)', roles: OPS },
      { to: '/pos-sales', icon: ShoppingCart, label: 'Sales (POS)',    roles: OPS_ACCT },
      { to: '/shifts',    icon: Clock,        label: 'Shifts (POS)',   roles: OPS_ACCT },
      { to: '/issues',   icon: Flag,  label: 'Issues',         roles: OPS, badgeKey: 'issues' },
      { to: '/support',  icon: MessageSquare, label: 'Support Inbox', roles: OPS, badgeKey: 'support' },
    ],
  },
  {
    // Phase 12 ERP surfaces. Keep small for now (AP + future P&L / cash
    // flow in Phase 12.4). Suppliers + Purchase Orders + Stock Counts
    // stay under Operations since they're inventory-first, not
    // accounting-first.
    id: 'finance',
    label: 'Finance',
    items: [
      { to: '/pnl',              icon: FileBarChart, label: 'P&L',              roles: OPS_ACCT },
      { to: '/cash-flow',        icon: Banknote,     label: 'Cash Flow',        roles: OPS_ACCT },
      { to: '/accounts-payable', icon: Receipt,      label: 'Accounts Payable', roles: OPS_ACCT },
    ],
  },
  {
    id: 'engagement',
    label: 'Engagement',
    items: [
      { to: '/experiences', icon: Sparkles,     label: 'Experiences', roles: OPS_MKT },
      { to: '/events',      icon: CalendarDays, label: 'Events',      roles: OPS_MKT },
      { to: '/bookings',    icon: Ticket,       label: 'All Bookings', roles: OPS_MKT },
      { to: '/catering',    icon: PartyPopper,  label: 'Catering',    roles: OPS_ACCT },
      { to: '/games',       icon: Trophy,       label: 'Games',       roles: OPS_MKT },
      { to: '/prizes',      icon: Gift,         label: 'Prizes',      roles: OPS_MKT },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { to: '/notifications',  icon: Bell,     label: 'Notifications',   roles: OPS_MKT },
      { to: '/marketing-site', icon: Globe,    label: 'Marketing Site',  roles: OPS_MKT },
      { to: '/settings',       icon: Settings, label: 'Settings',        roles: OPS },
      { to: '/admin-users',    icon: UserCog,  label: 'Admin Users',     superAdminOnly: true },
      { to: '/audit-log',      icon: FileSearch, label: 'Audit Log',     superAdminOnly: true },
      { to: '/data-purge',     icon: Trash2,   label: 'Data Purge',      superAdminOnly: true },
      { to: '/pci-tamper',     icon: Shield,   label: 'PCI Tamper Check',superAdminOnly: true },
      { to: '/kb',             icon: BookOpen, label: 'Knowledge Base',  roles: ALL },
    ],
  },
]
